import React from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Collapse,
  Backdrop,
} from "@mui/material";
import { ExpandLess, ExpandMore, Close } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";

type Languages = "de" | "en";
type MenuItem = {
  labels: { language: Languages; label: string }[];
  menuName: string;
  route: string;
  subItems?: MenuItem[];
};

type SidebarProps = {
  open: boolean;
  onClose: () => void;
  menuItems: MenuItem[];
  languageHandler: (menuItem: MenuItem, language: Languages) => string;
  currentLanguage: Languages;
};

const Sidebar: React.FC<SidebarProps> = ({
  open,
  onClose,
  menuItems,
  languageHandler,
  currentLanguage,
}) => {
  const [openSubmenus, setOpenSubmenus] = React.useState<{
    [key: string]: boolean;
  }>({});
  const navigate = useNavigate();
  const location = useLocation(); // Get the current route

  const toggleSubmenu = (menuName: string) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [menuName]: !prev[menuName],
    }));
  };

  const handleNavigation = (route: string) => {
    const path = `/${currentLanguage}${route}`;
    navigate(path);
    onClose(); // Close the sidebar after navigation
  };

  const isActive = (route: string) => {
    const path = `/${currentLanguage}${route}`;
    return location.pathname === path; // Check if the current route matches
  };

  const isSubmenuActive = (subItems?: MenuItem[]) => {
    if (!subItems) return false;
    return subItems.some((subItem) => isActive(subItem.route));
  };

  React.useEffect(() => {
    // Automatically open parent menus if a submenu is active
    const activeSubmenus: { [key: string]: boolean } = {};
    menuItems.forEach((item) => {
      if (isSubmenuActive(item.subItems)) {
        activeSubmenus[item.menuName] = true;
      }
    });
    setOpenSubmenus(activeSubmenus);
  }, [menuItems]);

  return (
    <>
      {/* Backdrop */}
      <Backdrop open={open} onClick={onClose} sx={{ zIndex: 1200 }} />

      {/* Sidebar Drawer */}
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        sx={{
          "& .MuiDrawer-paper": {
            width: 250,
            padding: "1rem",
          },
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{ alignSelf: "flex-end", marginBottom: "1rem" }}
        >
          <Close />
        </IconButton>

        {/* Menu Items */}
        <List>
          {menuItems.map((item) => (
            <React.Fragment key={item.menuName}>
              <ListItem
                component="div"
                onClick={() =>
                  item.subItems
                    ? toggleSubmenu(item.menuName)
                    : handleNavigation(item.route)
                }
                sx={{
                  backgroundColor: isActive(item.route)
                    ? "rgba(0, 0, 0, 0.1)"
                    : "inherit", // Highlight active item
                  borderLeft: (theme) =>
                    isActive(item.route) ? `4px solid #F8963B` : "none", // Add a border for active item
                }}
              >
                <ListItemText
                  primary={languageHandler(item, currentLanguage)}
                />
                {item.subItems ? (
                  openSubmenus[item.menuName] ? (
                    <ExpandLess />
                  ) : (
                    <ExpandMore />
                  )
                ) : null}
              </ListItem>

              {/* Submenu Items */}
              {item.subItems && (
                <Collapse
                  in={openSubmenus[item.menuName]}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem) => (
                      <ListItem
                        key={subItem.menuName}
                        sx={{
                          pl: 4,
                          backgroundColor: isActive(subItem.route)
                            ? "rgba(0, 0, 0, 0.1)"
                            : "inherit", // Highlight active sub-item
                          borderLeft: isActive(subItem.route)
                            ? "4px solid #F8963B"
                            : "none", // Add a border for active sub-item
                        }}
                        onClick={() => handleNavigation(subItem.route)}
                      >
                        <ListItemText
                          primary={languageHandler(subItem, currentLanguage)}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Drawer>
    </>
  );
};

export default Sidebar;
